'use client'
import {
  useAudioThumbnail,
  useIconThumbnail,
  useVideoThumbnail,
} from '@/hooks/use-asset-thumbnail'
import { parseFileNameFromUrl } from '@/hooks/use-upload-assets'
import type { Asset } from '@/lib/types'
import { generateCdnImageUrl } from '@/lib/utils'
import { useMemo, useState, useEffect } from 'react'
import { Database, Play, Music } from 'lucide-react'

interface IAssetThumbnailProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  asset: Omit<Asset, 'source'>
  className?: string
  width?: number
  height?: number
  hideFileName?: boolean
}

/**
 * 用于渲染上传文件的缩略图
 */
export const AssetThumbnail = ({
  asset,
  className,
  width = 200,
  height = 200,
  hideFileName = false,
  ...other
}: IAssetThumbnailProps) => {
  const { thumbnail: videoThumbnail } = useVideoThumbnail(
    asset.type === 'video' ? asset.link : '',
  )

  const { thumbnail: audioThumbnail } = useAudioThumbnail(
    asset.type === 'audio' ? asset.link : '',
  )

  const { thumbnail: iconThumbnail } = useIconThumbnail(
    asset.type === 'icon' ? asset.link : undefined,
  )

  const thumbnail = useMemo(() => {
    switch (asset.type) {
      case 'video':
        return videoThumbnail
      case 'audio':
        return audioThumbnail
      case 'icon':
        return iconThumbnail
      default:
        return asset.thumbnail
    }
  }, [videoThumbnail, audioThumbnail, iconThumbnail, asset])

  const isTableAsset = asset.type === 'table'
  const isTempAsset = asset.link?.startsWith('temp://')

  // For image assets, use link as thumbnail if thumbnail is not set
  const effectiveThumbnail = useMemo(() => {
    // If thumbnail is already set, use it
    if (thumbnail) return thumbnail
    // For image assets, the link itself is the image
    if (
      asset.type === 'image' &&
      asset.link &&
      !asset.link.startsWith('temp://')
    ) {
      return asset.link
    }
    return null
  }, [thumbnail, asset.type, asset.link])

  // Show thumbnail if it exists and is a valid URL (http/https/blob)
  const hasValidThumbnail =
    effectiveThumbnail &&
    (effectiveThumbnail.startsWith('http://') ||
      effectiveThumbnail.startsWith('https://') ||
      effectiveThumbnail.startsWith('blob:'))

  const [imageError, setImageError] = useState(false)

  // Reset error state when thumbnail changes
  useEffect(() => {
    setImageError(false)
  }, [effectiveThumbnail])

  // Check if using default placeholder thumbnail (not a real thumbnail)
  const isDefaultPlaceholder = useMemo(() => {
    if (!effectiveThumbnail) return true
    // Check if it's the default video/audio placeholder
    return (
      effectiveThumbnail.includes('design-system/video_') ||
      effectiveThumbnail.includes('design-system/audio_')
    )
  }, [effectiveThumbnail])

  // Render fallback placeholder
  const renderFallback = () => (
    <div
      className={`${className} flex items-center justify-center bg-white/10 rounded-lg border border-white/20`}
      {...other}
    >
      <span className="text-white/40 text-xs text-center px-2 truncate">
        {asset.name || 'File'}
      </span>
    </div>
  )

  // Render video placeholder with play icon
  const renderVideoPlaceholder = () => (
    <div
      className={`${className} flex items-center justify-center bg-gradient-to-br from-purple-600/80 to-pink-600/80 rounded-lg`}
      {...other}
    >
      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
        <Play className="w-5 h-5 text-white fill-white ml-0.5" />
      </div>
    </div>
  )

  // Render audio placeholder with music icon
  const renderAudioPlaceholder = () => (
    <div
      className={`${className} flex items-center justify-center bg-gradient-to-br from-emerald-600/80 to-teal-600/80 rounded-lg`}
      {...other}
    >
      <Music className="w-8 h-8 text-white" />
    </div>
  )

  return (
    <div className="relative group">
      {isTableAsset ? (
        // Table asset - render database icon proportional to container size
        <div
          className={`${className} flex items-center justify-center bg-white dark:bg-white rounded-lg`}
          {...other}
        >
          <Database className="w-10 h-10 text-gray-800 opacity-70" />
        </div>
      ) : asset.type === 'video' && (isDefaultPlaceholder || imageError) ? (
        // Video asset with default placeholder - render styled video icon
        renderVideoPlaceholder()
      ) : asset.type === 'audio' && (isDefaultPlaceholder || imageError) ? (
        // Audio asset with default placeholder - render styled audio icon
        renderAudioPlaceholder()
      ) : // Regular asset - render thumbnail image
      hasValidThumbnail && !imageError ? (
        <img
          src={generateCdnImageUrl(effectiveThumbnail, {
            width,
            height,
          })}
          className={className}
          onError={() => {
            // On error, show placeholder instead of hiding
            setImageError(true)
          }}
          {...other}
        />
      ) : (
        // Fallback: show placeholder for temp/broken assets or when image fails to load
        renderFallback()
      )}
      {!hideFileName && (
        <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity bg-black/75 text-white text-xs p-1 bottom-0 left-0 right-0 truncate">
          {isTableAsset
            ? asset.name || 'Table'
            : parseFileNameFromUrl(asset.link)}
        </div>
      )}
    </div>
  )
}
